import { useState } from "react";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useFleetMetrics } from "@/api/hooks";
import { DiffBadge } from "@/components/diff-badge";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { TeamFlag } from "@/components/team-flag";
import { RecordingGrids } from "@/components/metrics-param-grid";
import {
  METRICS_KIND_ORDER,
  METRICS_KINDS,
  paramsToMap,
  type MetricsKind,
} from "@/domain/metrics-params";
import { timeAgo } from "@/lib/time";
import { cn } from "@/lib/utils";
import type { components } from "@/api/generated/schema";

type TeamEntry = components["schemas"]["FleetMetricsTeamEntry-Output"];
type KindEntry = components["schemas"]["FleetMetricsKindEntry-Output"];

// Header and TeamRow must stay column-aligned — one template, used in both.
const FLEET_GRID = "grid-cols-[minmax(9rem,1.4fr)_repeat(3,minmax(7rem,1fr))_1.5rem]";

function KindChip({ entry }: { entry: KindEntry }) {
  if (!entry.latest) {
    return <span className="text-xs text-muted-foreground/50">—</span>;
  }
  return (
    <span className="inline-flex items-baseline gap-1.5 text-xs whitespace-nowrap">
      <span className="text-foreground/80">{timeAgo(entry.latest.fetched_at)}</span>
      <DiffBadge count={entry.diff_count} />
    </span>
  );
}

function TeamRow({ team }: { team: TeamEntry }) {
  const [expanded, setExpanded] = useState(false);
  const kindEntry = (kind: MetricsKind): KindEntry => team[kind];
  const hasAny = METRICS_KIND_ORDER.some((k) => kindEntry(k).latest);

  return (
    <div>
      <button
        onClick={() => hasAny && setExpanded(!expanded)}
        disabled={!hasAny}
        className={cn(
          `grid ${FLEET_GRID} items-center gap-3 w-full`,
          "bg-card border-l-3 pl-4 pr-3 h-12 text-left transition-shadow",
          hasAny
            ? "border-l-emerald-500 hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
            : "border-l-border cursor-default",
          expanded ? "" : "rounded-r-lg",
        )}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          <TeamFlag iso_alpha2={team.iso_alpha2} size="sm" />
          <span className="text-sm font-medium truncate">{team.team_name}</span>
          {team.boat_code && (
            <span className="text-xs text-muted-foreground">{team.boat_code}</span>
          )}
        </span>
        {METRICS_KIND_ORDER.map((kind) => (
          <KindChip key={kind} entry={kindEntry(kind)} />
        ))}
        <span className="justify-self-end">
          {hasAny &&
            (expanded ? (
              <IconChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <IconChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ))}
        </span>
      </button>

      {expanded && (
        <div className="bg-card border-l-3 border-l-emerald-500 rounded-br-lg pl-4 pr-5 py-4 space-y-6 overflow-x-auto">
          {METRICS_KIND_ORDER.map((kind) => {
            const entry = kindEntry(kind);
            if (!entry.latest) return null;
            return (
              <div key={kind}>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  {METRICS_KINDS[kind].label}
                  <span className="ml-2 normal-case font-normal tracking-normal">
                    {entry.event_name} · {timeAgo(entry.latest.fetched_at)} by{" "}
                    {entry.latest.fetched_by}
                  </span>
                </div>
                <RecordingGrids kind={kind} params={paramsToMap(entry.latest.parameters)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function FleetMetricsPage() {
  const { data, isLoading } = useFleetMetrics();

  return (
    <div>
      <PageHeader
        title="Bot Metrics"
        subtitle="Latest wing bot, rake bot, and HMI recordings per boat — an audit trail of the control-system sign-off metrics."
      />

      <div className="mt-6">
        <div className={`grid ${FLEET_GRID} gap-3 pl-4 pr-3 pb-2`}>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Team
          </span>
          {METRICS_KIND_ORDER.map((kind) => (
            <span
              key={kind}
              className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {METRICS_KINDS[kind].label}
            </span>
          ))}
          <span />
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-r-lg" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {data?.teams.map((team) => <TeamRow key={team.iso_alpha2} team={team} />)}
          </div>
        )}
      </div>
    </div>
  );
}
